import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface SuperHit {
  id: string;
  url: string;
  services: string[];
  credentials?: Record<string, unknown>;
  verificationResult?: any;
}

interface SuperHitContextType {
  showSuperHitPopup: boolean;
  currentSuperHit: SuperHit | null;
  triggerSuperHit: (hit: SuperHit) => void;
  closeSuperHitPopup: () => void;
}

const SuperHitContext = createContext<SuperHitContextType | undefined>(undefined);

export const useSuperHit = () => {
  const context = useContext(SuperHitContext);
  if (context === undefined) {
    throw new Error('useSuperHit must be used within a SuperHitProvider');
  }
  return context;
};

interface SuperHitProviderProps {
  children: ReactNode;
}

export const SuperHitProvider: React.FC<SuperHitProviderProps> = ({ children }) => {
  const [showSuperHitPopup, setShowSuperHitPopup] = useState(false);
  const [currentSuperHit, setCurrentSuperHit] = useState<SuperHit | null>(null);
  const [triggeredHits, setTriggeredHits] = useState<string[]>([]);

  const triggerSuperHit = (hit: SuperHit) => {
    // Éviter de déclencher plusieurs fois le même hit
    if (triggeredHits.includes(hit.id)) {
      return;
    }
    
    setCurrentSuperHit(hit);
    setShowSuperHitPopup(true);
    setTriggeredHits(prev => [...prev, hit.id]);
    
    // Auto-fermeture après 10 secondes
    setTimeout(() => {
      setShowSuperHitPopup(false);
      setCurrentSuperHit(null);
    }, 10000);
  };

  const closeSuperHitPopup = () => {
    setShowSuperHitPopup(false);
    setCurrentSuperHit(null);
  };

  return (
    <SuperHitContext.Provider value={{
      showSuperHitPopup,
      currentSuperHit,
      triggerSuperHit,
      closeSuperHitPopup
    }}>
      {children}
    </SuperHitContext.Provider>
  );
};
